import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  GetInstalledAppDetailViewModel,
  GetInstalledAppsViewModel,
  ProblemDetails,
} from "../../api/axcloud";
import {
  createContractInstalledApplicationApi,
  isAxiosErrorProblemDetails,
} from "../../api";
import { logout } from "../login/loginSlice";
import { CancelToken } from "axios";

//#region Thunk

/**
 * 連携アプリ一覧取得に必要な情報
 */
type FetchInstalledAppsArgs = {
  accessToken: string;
  cancelToken: CancelToken;
};

/**
 * 連携アプリ一覧を取得します。
 */
export const fetchInstalledApps = createAsyncThunk<
  GetInstalledAppsViewModel,
  FetchInstalledAppsArgs,
  {
    rejectValue: ProblemDetails;
  }
>("installedApplication/fetchInstalledApps", async (arg, thunk) => {
  const api = createContractInstalledApplicationApi(arg.accessToken);

  // 連携アプリの一覧を取得する
  return api
    .getInstalledAppsAsync({ cancelToken: arg.cancelToken })
    .then((res) => {
      return res.data;
    })
    .catch((err) => {
      // 想定されるエラーはAxiosエラー
      if (isAxiosErrorProblemDetails(err)) {
        return thunk.rejectWithValue(err.response!.data);
      } else {
        throw err;
      }
    });
});

// ---------------------------------------------------------------------------------

/**
 * 連携アプリ詳細取得に必要な情報
 */
type FetchInstalledAppDetailArgs = {
  accessToken: string;
  applicationId: string;
  cancelToken: CancelToken;
};

/**
 * 連携アプリの詳細を取得します。
 */
export const fetchInstalledAppDetail = createAsyncThunk<
  GetInstalledAppDetailViewModel,
  FetchInstalledAppDetailArgs,
  {
    rejectValue: ProblemDetails;
  }
>("installedApplication/fetchInstalledAppDetail", async (arg, thunk) => {
  const api = createContractInstalledApplicationApi(arg.accessToken);

  // 単体の連携アプリに対して詳細を取得する
  return api
    .getInstalledAppDetailAsync(arg.applicationId, {
      cancelToken: arg.cancelToken,
    })
    .then((res) => {
      return res.data;
    })
    .catch((err) => {
      // 想定されるエラーはAxiosエラー
      if (isAxiosErrorProblemDetails(err)) {
        return thunk.rejectWithValue(err.response!.data);
      } else {
        throw err;
      }
    });
});

// ---------------------------------------------------------------------------------

/**
 * 連携アプリ削除に必要な情報
 */
type DeleteInstalledAppArgs = {
  accessToken: string;
  applicationId: string;
  cancelToken: CancelToken;
};

/**
 * 連携アプリを削除します。
 */
export const deleteInstalledApp = createAsyncThunk<
  void,
  DeleteInstalledAppArgs,
  {
    rejectValue: ProblemDetails;
  }
>("installedApplication/deleteInstalledApp", async (arg, thunk) => {
  const api = createContractInstalledApplicationApi(arg.accessToken);

  // 連携アプリを削除する
  return api
    .deleteInstalledAppAsync(arg.applicationId, {
      cancelToken: arg.cancelToken,
    })
    .then((res) => {
      return res.data;
    })
    .catch((err) => {
      if (isAxiosErrorProblemDetails(err)) {
        return thunk.rejectWithValue(err.response!.data);
      } else {
        throw err;
      }
    });
});

// ---------------------------------------------------------------------------------

// TODO: 連携アプリ追加

//#endregion

export type InstalledAppState = {
  installedApps: GetInstalledAppsViewModel;
  selectInstalledApp: GetInstalledAppDetailViewModel | null;
  isLoadings: {
    fetchInstalledApps: boolean;
    fetchInstalledAppDetail: boolean;
    deleteInstalledApp: boolean;
  };
  paging: {
    currentPage: number;
    rowsPerPage: number;
  };
};

const initialState: InstalledAppState = {
  installedApps: {
    installedApplications: [],
    meta: { total: 0 },
  },
  selectInstalledApp: null,
  isLoadings: {
    fetchInstalledApps: false,
    fetchInstalledAppDetail: false,
    deleteInstalledApp: false,
  },
  paging: {
    currentPage: 0,
    rowsPerPage: 5,
  },
};

export const installedAppSlice = createSlice({
  name: "installedApplication",
  initialState: initialState,
  reducers: {
    // 連携アプリ詳細をクリアする
    clearInstalledAppDetail: (state) => ({
      ...state,
      selectInstalledApp: null,
    }),

    //現在のページをセットする
    setCurrentPage: (state, action: PayloadAction<number>) => ({
      ...state,
      paging: {
        ...state.paging,
        currentPage: action.payload,
      },
    }),

    //ページ毎の行数をセットする
    setRowsPerPage: (state, action: PayloadAction<number>) => ({
      ...state,
      paging: {
        ...state.paging,
        rowsPerPage: action.payload,
      },
    }),

    // ページング情報をリセットする
    resetPaging: (state) => ({ ...state, paging: initialState.paging }),
  },
  extraReducers: (builder) => {
    //#region pending

    // 連携アプリ一覧取得中
    builder.addCase(fetchInstalledApps.pending, (state) => {
      return {
        ...state,
        isLoadings: {
          ...state.isLoadings,
          fetchInstalledApps: true,
        },
      };
    });
    // 連携アプリ詳細取得中
    builder.addCase(fetchInstalledAppDetail.pending, (state) => {
      return {
        ...state,
        isLoadings: {
          ...state.isLoadings,
          fetchInstalledAppDetail: true,
        },
      };
    });
    // 連携アプリ削除中
    builder.addCase(deleteInstalledApp.pending, (state) => {
      return {
        ...state,
        isLoadings: {
          ...state.isLoadings,
          deleteInstalledApp: true,
        },
      };
    });

    //#endregion

    //#region fulfilled

    // ログアウト時にリセットする
    builder.addCase(logout.fulfilled, () => ({
      ...initialState,
    }));
    // 連携アプリ一覧取得成功時
    builder.addCase(fetchInstalledApps.fulfilled, (state, action) => {
      return {
        ...state,
        installedApps: {
          installedApplications: action.payload.installedApplications,
          meta: action.payload.meta,
        },
        isLoadings: {
          ...state.isLoadings,
          fetchInstalledApps: false,
        },
      };
    });
    // 連携アプリ詳細取得成功時
    builder.addCase(fetchInstalledAppDetail.fulfilled, (state, action) => {
      return {
        ...state,
        selectInstalledApp: action.payload,
        isLoadings: {
          ...state.isLoadings,
          fetchInstalledAppDetail: false,
        },
      };
    });
    // 連携アプリ削除成功時
    builder.addCase(deleteInstalledApp.fulfilled, (state) => {
      return {
        ...state,
        selectInstalledApp: null,
        isLoadings: {
          ...state.isLoadings,
          deleteInstalledApp: false,
        },
      };
    });
    //#endregion

    //#region rejected

    // 連携アプリ一覧取得失敗時
    builder.addCase(fetchInstalledApps.rejected, (state) => {
      return {
        ...state,
        isLoadings: {
          ...state.isLoadings,
          fetchInstalledApps: false,
        },
      };
    });
    // 連携アプリ詳細取得失敗時
    builder.addCase(fetchInstalledAppDetail.rejected, (state) => {
      return {
        ...state,
        selectInstalledApp: null,
        isLoadings: {
          ...state.isLoadings,
          fetchInstalledAppDetail: false,
        },
      };
    });
    // 連携アプリ削除失敗時
    builder.addCase(deleteInstalledApp.rejected, (state) => {
      return {
        ...state,
        isLoadings: {
          ...state.isLoadings,
          deleteInstalledApp: false,
        },
      };
    });

    //#endregion
  },
});

export const {
  clearInstalledAppDetail,
  setCurrentPage,
  setRowsPerPage,
  resetPaging,
} = installedAppSlice.actions;

export default installedAppSlice.reducer;
